/**
 * My requests screen (active tab 2 of 2): GET /api/requests, home-scoped
 * list. No price figure is ever rendered.
 *
 * ⭐⭐ T2073 M2 (board 2073, slice 1a; D7/D8/D9, R-2/R-7/R-8/R-14) — THREE
 * CHANGES TO WHAT THIS SCREEN SHOWS AND WHEN. (1) UPCOMING ONLY, SOONEST FIRST:
 * the list is the rows dated today or later in Europe/Dublin, ascending; a row
 * with no readable day (the legacy pattern row, a malformed date) is SHOWN, never
 * silently hidden (Captain ruling). The server order (newest first) is no longer
 * the screen order. (2) THE LIST REFRESHES ITSELF while the tab is visible AND
 * the nurse is attending it (`live`, from the shell's idle nudge), every
 * `LIST_REFRESH_MS`, at once on return to the tab, and on the first touch after
 * idle — and never while unattended, because every authenticated request slides
 * the session's `last_active_at` (`auth-repo.ts`) and a poll that ran on an
 * unattended tab would keep a session alive that the idle window exists to end
 * (`SESSION_IDLE_MS`, 300 s since T2075 M6; the shell locks the screen at 120 s
 * of no touch, `app.tsx`). (3) NO UPCOMING ROWS is the shared `EmptyState` (neutral), whether the
 * nurse has none or only past ones. And R-2: the card no longer renders the
 * derived `Pickup` row — see `whenFor`.
 *
 * ⭐ T1723 M7 — THE ROW IS `Card`'s REQUEST SHAPE (Card A, Vicky qa-2026-09-05,
 * confirmed at the sitting 2026-09-10; fence lifted for this file the same day).
 * ⚠️ IT IS NOT `RequestCard` ANY MORE, AND THAT FILE NO LONGER EXISTS (T1749 M7
 * Step 3, `7380e521`): the shared `RequestCard` was ABSORBED into `Card` so one
 * component carries the row and the card, and this screen composes `CardRequest`
 * from `@ecmb/ui`. Corrected at T1749 M7 rework 1; the sentence below describes
 * what that shape renders, which did not change with the move.
 * One `<li>` per request, holding one card: the status pill (`labelFor`, Words 3,
 * or `Seen` once the office has seen it, board 2707 M4) leading a waiting-on
 * sentence, the four-node step line (Sent / Seen / Confirmed / Travelled) lit at
 * the request's TRUTHFUL current node, D2's facts table with the pickup big and the
 * appointment small, and a centred full-width cancel with the two-tap confirm
 * IN PLACE. The four-track grid this row used to be (`app.css`'s T769/1077/
 * T1717 history) is retired with it; the card stacks, so nothing can starve a
 * track at any width.
 *
 * ⛔ WORDS 3 ON THIS SURFACE, AND THE SIX-STAGE SPINE IS SUPERSEDED HERE. The
 * card derives BOTH the word and the lit step from the one `status` value, so
 * an account-priced request reads `Sent` with `Sent` lit — the nurse never sees
 * a quote stage. ⭐ T1749 M7 CLOSED THE OTHER HALF: the server no longer projects
 * `lifecycle` at all. T1723 M7 could only stop READING it (the projection was
 * fenced then) and recorded the projected-but-unrendered field as owed; M7 owns
 * the projection, so the field and `nurseLifecycle()` are both gone and the wire
 * carries what the screen renders again. `myRequestsCopy.operatorStatusLabels`
 * is Helen's portal-table map only (`Ops.tsx`), and since M7 Step 2 it reads
 * `New` / `Priced` — the kiosk's operator words.
 *
 * ⭐⭐ T1749 M7 — THE SCREEN COMPOSES `@ecmb/ui` AND DRAWS NOTHING. `CardRequest`
 * (the absorbed Card A; the `RequestCard` alias is deleted with this commit),
 * `Chip`'s filter shape for the day filter, and `Button` for the cancel and its
 * confirm limbs (since board 2732 M3 those limbs live in `../request-actions.tsx`,
 * shared with All requests). The three carry-ins from the 2026-09-10 live drive
 * land here: the pickup is on her wire and BIG in the facts table, `Asked for by`
 * says `you` or `a colleague` and never a name, and her own `Notes` come back to
 * her (a colleague's do not for a ward account; since board 2732 M2, reception
 * and admin read every row's notes behind the cross-group switch, ADR-0008 —
 * the projection decides that, not this screen).
 *
 * ⭐ THE DAY FILTER (T1723 M7; SC-C1..C4). Component state only: nothing is
 * read from or written to the page URL, its query string or its hash
 * (spec-asserted over the whole client, and the plan's raw grep for the three
 * DOM spellings must print 0, which is why they are not named here). One parse
 * (`format.ts`'s Dublin day number), the same zone the card displays in. `All`
 * is the default, so it is UX and not a minimisation control: every row has
 * already crossed the wire. A legacy pattern row (`journeyDate: null`) has no
 * single day, so it is listed under `Repeating trips` in EVERY bucket rather
 * than computed onto one — computing a next occurrence in the browser would be
 * business logic on the wrong side of the projection, and hiding the row would
 * make a standing run vanish. A row whose date cannot be read costs itself its
 * bucket, never the screen its list, and is always under `All`.
 *
 * Cancel (P5; board 2788 R-4): a two-tap cancel on a New or Priced request only.
 * A Confirmed card asks to change or to cancel (a repeating trip asks to stop),
 * and inside 24 hours of the pickup the card sends the home to the phone (DD-19).
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ApiClient } from '../api.js';
import type {
  AskKind,
  AskState,
  DeclineReason,
  RequestStatus,
  ReturnAtState,
  SchedulePattern,
  TimeMeaning,
} from '../../shared/request-schema.js';
import { dublinDayNumber, formatReturnAt, sameDublinDay, sameDublinWeek } from '../format.js';
import { myRequestsCopy, requestSeenCopy, sharedCopy } from '../copy.js';
import { useRequestActions, type RequestHistoryView } from '../request-actions.js';
import { CardRequest, Chip, EmptyState, formatWhenParts, type CardRequestWhen } from '@ecmb/ui';

/** How long a load may take before the screen offers a retry (DESIGN.md §7.1). */
const SLOW_LOAD_MS = 5_000;

/** ⭐ T2073 M2 (D8) — the background refresh cadence while the tab is visible
 *  and attended: the kiosk's own `Bookings.tsx` `LIST_REFRESH_MS` cadence, so
 *  a status Helen sets there reaches the nurse on the same clock.
 *  ⭐ T2075 M6 — EXPORTED for the shell's idle-triple bound (`app.tsx`,
 *  plan D14): the nudge plus one tick must land before the auto-lock, and the
 *  keepalive plus one tick before the server's idle window. */
export const LIST_REFRESH_MS = 15_000;

/**
 * ⭐⭐ board 2707 (M4, plan DD5) — WHAT ONE ROW HANDS THE CARD ABOUT SEEN,
 * derived ONCE for both lists: this screen and `HomeRequests.tsx` call it, so
 * the nurse and reception can never be told two different things.
 *
 * `seen` is DD5's derivation: the wire's `seen` bit, OR a status only the office
 * door can write (`priced`, `confirmed`, `declined`), because a request in one of
 * those states was opened by the office even if it predates v16's column. A
 * `cancelled` one derives nothing (the nurse can cancel), so it is seen only on
 * the wire bit. ⛔ The bit is read with `=== true`: absent (an older payload) and
 * anything else mean "not seen", and a list row never throws.
 * ⛔ No time: board 2732 (Alex, 2026-10-02) removed the `Seen` fact row so the
 * portal does not read as monitoring the office; the wire no longer carries it.
 * `waitingOn` is the seen sentence for a seen request the office has not yet
 * confirmed (`new` / `priced`), and the status's own sentence otherwise.
 */
export function seenFor(
  status: RequestStatus,
  seenBit: boolean | undefined,
): { seen: boolean; waitingOn: string } {
  const seen = seenBit === true || status === 'priced' || status === 'confirmed' || status === 'declined';
  return {
    seen,
    waitingOn: seen && (status === 'new' || status === 'priced')
      ? requestSeenCopy.waitingOnSeen
      : myRequestsCopy.waitingOn[status],
  };
}

type RequestListItem = {
  requestId: number;
  /** ⭐ T1683 M3 (A1) - the one reference for this job, or null on every
   *  request written before M3 (there is no backfill). The card renders no
   *  reference line at all when it is null. ⚠️ Board 2707 M6 re-took "opaque":
   *  a linked account's request carries its readable code and number (`LCC 05`). */
  jobRef: string | null;
  residentDisplayName: string;
  destinationName: string;
  /** ⭐ T1683 M7 (A4) - her own answer read back. This local type is the render
   *  contract for the row: a field left off here is invisible to the nurse no
   *  matter what the endpoint sends. */
  returnTrip: boolean;
  /** ⭐⭐ T1717 M5 (DD4, P0-7) - WHEN the bus brings them back, as an ISO
   *  instant. Null on every one-way job, on a REPEATING return job (no instant
   *  is captured for a pattern) and on every request written before schema v11 -
   *  the phrase then reads "and back" alone. Same render contract as above. */
  returnAt: string | null;
  /** ⭐⭐ T2088 M4 (ADR-0006, plan D3) - WHICH null the one above is. `'to-confirm'`
   *  means the nurse said the return time is not yet known and Helen will ring:
   *  the row reads "and back time to confirm". `'unset'` is every other row - an
   *  instant present, no return leg, or a return written before v14 - and reads
   *  exactly as before this milestone. On the wire unconditionally since M2
   *  (`core/nurse-request-projection.ts`); rendered from here. */
  returnAtState: ReturnAtState;
  journeyDate: string | null;
  schedulePattern: SchedulePattern | null;
  /** T632. The meaning decides WHICH fact row the stated time sits in on the
   *  card (Pickup big / Appointment small / Time neutral). */
  timeMeaning: TimeMeaning | null;
  status: RequestStatus;
  /** ⭐⭐ T1749 M7 (R2) — WHEN THE BUS COLLECTS HER, as an ISO instant, or null.
   *  Null on every `collection` run (the stated time IS the pickup and arrives
   *  through `timeMeaning`) and on every pre-v12 row.
   *  ⛔ T2073 M2 (INITIAL R-2, Alex, board 2073): STILL ON THE WIRE, NO LONGER
   *  RENDERED. Since M1 the form derives this from the home's offset and it is
   *  Helen's working figure, not the nurse's answer; `whenFor` reads it no more.
   *  The key stays here because it is in the projection's keep-set (S6/S7) and
   *  the render contract must name what arrives, rendered or not. */
  pickupAt: string | null;
  /** ⭐ T1749 M7 (R2) — TWO WORDS, DERIVED ON THE SERVER, NEVER A NAME. The
   *  comparison against the session nurse happens in
   *  `core/nurse-request-projection.ts`; this screen renders the answer and has
   *  no id to compare with, which is the property that keeps a colleague's
   *  identity off the wire. */
  askedForBy: 'you' | 'a colleague';
  /** ⭐⭐ T2075 M3 (plan A2, board 1864; ruling R-2075-a in OPEN-QUESTIONS) —
   *  THE STAFF MEMBER WHO ASKED, by name, or null on a row written before the
   *  picker (every pre-v13 row). This is a NEW fact, not the account: the
   *  colleague who ENTERED the request is still the two words above and her
   *  display name is still the projection canary (`nurse-request-projection.ts`
   *  `NURSE_REQUEST_KEEP_KEYS`). Optional on this contract because a mirror
   *  that predates M2 sends no key, and the slot then falls back to the two
   *  words - which is exactly the legacy render, never a blank. */
  requestedBy?: string | null;
  /** ⭐ board 2707 (M1, R-1) — when the request was made, an ISO instant, or
   *  null; rendered as the card's `Requested` row in Dublin time. On the wire
   *  since the same change (`core/nurse-request-projection.ts`). */
  createdAt: string | null;
  /** ⭐ board 2732 (M1, R-10) — whether East Coast Minibus has seen it, never
   *  when; read by `seenFor` (above) into the card's `Seen` node and its pill
   *  word. Optional on this contract because an older payload carries no key,
   *  and `seenFor` reads an absent key as not seen. */
  seen?: boolean;
  /** ⛔ T1749 M7 (R2) — PRESENT ON HER OWN ROW, and (board 2732 M2, ADR-0008)
   *  on every row for a reception / admin session at a switch-ON home; ABSENT
   *  otherwise. Optional here because it is optional on the WIRE: the server
   *  decides and simply omits the key, so the card's slot renders nothing
   *  without this screen deciding anything. */
  notes?: string;
  /** ⭐ Board 2788 M5 - the change view (`RequestChangeView`, on the wire since
   *  M3): whether the home changed it, the one ask slot and the history,
   *  rendered by `request-actions.tsx`. Optional for `seen`'s reason. */
  homeChanged?: boolean;
  askKind?: AskKind | null;
  askState?: AskState;
  askJourneyDate?: string | null;
  askPickupAt?: string | null;
  askDeclineReason?: DeclineReason | null;
  history?: readonly RequestHistoryView[];
  // ⛔ `lifecycle` IS NOT ON THIS TYPE, AND SINCE T1749 M7 IT IS NOT ON THE WIRE
  // EITHER. T1723 M7 could only stop reading it; the projection is M7's file and
  // the field is deleted there, so the render contract and the payload agree.
};

type DayFilter = 'today' | 'week' | 'all';

const FILTERS: ReadonlyArray<{ key: DayFilter; word: string }> = [
  { key: 'today', word: myRequestsCopy.filterToday },
  { key: 'week', word: myRequestsCopy.filterWeek },
  { key: 'all', word: myRequestsCopy.filterAll },
];

/**
 * Does a DATED row fall in the bucket? `All` never parses; the two day buckets
 * share `format.ts`'s one Dublin parse with the card's own day rendering.
 * Exported for the spec; pure.
 */
export function inDayBucket(journeyDate: string | null, filter: DayFilter, nowIso: string): boolean {
  if (filter === 'all') return true;
  if (journeyDate === null) return false;
  return filter === 'today' ? sameDublinDay(journeyDate, nowIso) : sameDublinWeek(journeyDate, nowIso);
}

/**
 * The card's facts from the row: the day and the ONE stated time placed by its
 * meaning. `collection` IS the pickup (big); `appointment` is the appointment
 * (small); an uncaptured meaning is said under the neutral `Time`, and a time
 * that cannot be read is said too ("a missing time is SAID, never left blank",
 * `format-when.ts`).
 *
 * ⛔⛔ T2073 M2 (2026-09-13, Alex, board 2073, slice 1a; INITIAL R-2) — THE
 * DERIVED `Pickup` ROW IS GONE FROM THE CARD, and this block records the
 * reversal rather than editing around it. T1749 M7 (R2) put `pickupAt` in the
 * keep-set and rendered it BIG beside the small `Appointment`, reversing in turn
 * the T1868 M5 paragraph that had measured it out. Since M1 of this task the
 * form asks the appointment time only and DERIVES the collection from the home's
 * own offset (`request-form-logic.ts`'s `derivePickup`): the instant on the wire
 * is the form's guess for Helen to work from, not an answer the nurse gave and
 * not one Helen has confirmed. R-2 says the nurse SHALL NOT see that guess, so
 * an appointment card renders `Appointment` only. The `collection` branch stays:
 * a nurse-stated pickup on a legacy row is her own answer. `pickupAt` stays on
 * the wire and on `RequestListItem` (S6/S7: the keep-set is unchanged) and is
 * simply not read here. The row may return in slice 2 as HELEN'S confirmed
 * collection, written back to the request — an open point for Alex in
 * `docs/OPEN-QUESTIONS.md` (T2073 rulings section).
 * ⛔ IT IS STILL NEVER INVENTED: nothing here computes a pickup from an
 * appointment, which is the office's arithmetic and not the browser's.
 * ⛔ AND IT READS IN THE PINNED ZONE. `formatWhenParts` is `en-IE` /
 * `Europe/Dublin`, the same pair the day and the return leg use, because one
 * card showing two clocks an hour apart is the defect `format.ts` records.
 * ⚠️ THE ROW'S HEIGHT CEILING IS MEASURED IN ONE PLACE AND ONLY ONE:
 * `e2e/tests/portal/portal-narrow-layout.spec.ts`, beside the caps, which is the
 * only layer that can measure a rendered box. Those caps were measured WITH the
 * `Pickup` row and stand as upper bounds; a milestone that adds a fact row to
 * this card owes a new measurement THERE; do not quote a figure here.
 */
export function whenFor(item: {
  journeyDate: string | null;
  /** ⭐ BACK ON THE SIGNATURE AT BOARD 2188. T2073 M2 removed it with the branch;
   *  restoring the branch restores the parameter. Named explicitly, never widened
   *  to the row, for the pinned-pair reason the comment below gives. */
  pickupAt: string | null;
  schedulePattern: SchedulePattern | null;
  timeMeaning: TimeMeaning | null;
}): CardRequestWhen {
  // ⛔ A LITERAL, NEVER THE ROW (security-review at M7). `WhenInput` also accepts
  // `timeZone` / `locale`; passing the wire object would let a row carrying
  // either override the pinned Europe/Dublin / en-IE pair while the day filter
  // buckets in a hard-coded Dublin — the bucket-vs-display split this screen
  // exists to prevent. The projection is a PICK today, so nothing carries them;
  // this keeps the pin by construction rather than by a distant file.
  const parts = formatWhenParts({
    journeyDate: item.journeyDate,
    schedulePattern: item.schedulePattern,
    timeMeaning: item.timeMeaning,
  });
  const when: CardRequestWhen = { day: parts.date };
  if (parts.clock === null) when.time = myRequestsCopy.timeNotStated;
  else if (item.timeMeaning === 'collection') when.pickup = parts.clock;
  else if (item.timeMeaning === 'appointment') when.appointment = parts.clock;
  else when.time = parts.clock;
  // ⭐⭐ RESTORED AT BOARD 2188 (Alex, 2026-09-18). The note that stood here said
  // the `if (item.pickupAt !== null)` branch was "deleted, not disabled" under
  // T2073 M2 (R-2), because the nurse SHALL NOT see the derived collection. That
  // ruling is REVERSED: a collection she cannot see is worse than a guess she can,
  // since the bus calls before the appointment.
  // ⛔ ON THE `appointment` BRANCH ONLY. The branches above are exclusive, so on a
  // `collection` row `when.pickup` already holds her own stated time — and such a
  // row never carries a `pickupAt` anyway (the validator refuses the pair by name).
  // That exclusivity is why this needs no new word and no `Card.tsx` change.
  // ⛔ A LITERAL, NEVER THE ROW, for the reason the block above gives.
  // ⚠️ Null on every pre-v12 row and every `collection` run, hence the guard.
  if (item.timeMeaning === 'appointment' && item.pickupAt !== null) {
    // ⛔⛔ THE DAY RIDES WITH IT WHEN IT DIFFERS (security review M1, board 2188).
    // The card has ONE day slot and it belongs to the APPOINTMENT, so a bare
    // clock here silently borrows the wrong date. That is reachable on
    // LEGITIMATE data, with no crafted body: the database allows a pickup offset
    // up to 480 minutes, so a 07:30 appointment derives 23:30 the night before,
    // and the card would read `Tue 20 Oct · Pickup 23:30` for a bus that called
    // on the Monday. Measured, not theorised.
    // ⚠️ The day is appended to the VALUE rather than given its own slot: the
    // shared `CardRequestWhen` has four fixed slots and adding a fifth is a
    // design-system change this row may not make.
    const pickupParts = formatWhenParts({ journeyDate: item.pickupAt });
    if (pickupParts.clock !== null) {
      const sameDay = dublinDayNumber(item.pickupAt) === dublinDayNumber(item.journeyDate);
      when.pickup = sameDay ? pickupParts.clock : `${pickupParts.clock} (${pickupParts.date})`;
    }
  }
  return when;
}

export type MyRequestsProps = {
  api: ApiClient;
  /** ⛔ T2073 M2 (D8) — IS THE NURSE ATTENDING THE TAB? Default `true`. The
   *  shell passes `!idle` from its idle nudge (`app.tsx`'s `useIdleNudge`, reset
   *  on `pointerdown` / `keydown`). The poll must not slide `last_active_at`
   *  (`auth-repo.ts`, the `SESSION_IDLE_MS` idle window, 300 s since T2075 M6:
   *  every authenticated request slides it) on an unattended tab, and the
   *  nudge is the client-side idle signal — so it gates the tick (the shell's
   *  auto-lock at 120 s, T2075 M6, is the same timer's second stage). `false` stops the interval
   *  and the visibility listener; the flip back to `true` is one refetch. */
  live?: boolean;
};

export function MyRequests({ api, live = true }: MyRequestsProps) {
  const [items, setItems] = useState<RequestListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DayFilter>('all');
  // §7.1: a load that has taken longer than SLOW_LOAD_MS offers a retry.
  const [slow, setSlow] = useState(false);
  // ⭐ ONE COUNTER, TWO JOBS (code-review at M7). `loadCount` is state, so the
  // timer effect below re-arms on EVERY load, including a retry pressed during
  // a slow load (an effect keyed on `[items, error]` alone armed once per
  // mount: a second slow wait never re-offered Retry). `latestLoad` is the same
  // number as a ref, so a response from an EARLIER request that lands after a
  // later one is dropped rather than overwriting the newer list.
  const [loadCount, setLoadCount] = useState(0);
  const latestLoad = useRef(0);
  // ⭐ T2073 M2 (D8) — the two refs the background refresh keeps. `halted` is set
  // by a 401 / 403 / 429 tick, so a session the server has ended (or throttled)
  // is not polled at on the interval; it is cleared by EVERY resume path — the
  // return to the tab (`onVisible`), the idle→active flip (`live` false→true,
  // the effect below) and Try again (`load()`) — because on a single-tab device
  // that is never backgrounded (the nursing-home iPad) the visibility event may
  // never fire, and a transient 429 must not leave the list stale for good
  // (security-review, M2 round 1). `firstRunDone` folds the mount fetch into the
  // `[live]` effect so the first paint issues exactly ONE GET whatever `live`
  // starts as.
  const haltedRef = useRef(false);
  const firstRunDone = useRef(false);

  const load = async (): Promise<void> => {
    const seq = latestLoad.current + 1;
    latestLoad.current = seq;
    haltedRef.current = false; // Try again is a resume path (see the refs above)
    setLoadCount(seq);
    setError(null);
    setSlow(false);
    const res = await api.get<{ requests: RequestListItem[] }>('/api/requests');
    if (seq !== latestLoad.current) return; // a newer load owns the screen now
    if (res.kind === 'network-error') {
      setError(sharedCopy.networkError);
      return;
    }
    if (res.status !== 200) {
      setError(myRequestsCopy.loadError);
      return;
    }
    setItems(res.body.requests);
  };

  /** ⭐ T2073 M2 (D8) — THE BACKGROUND TICK. The same GET and the same `latestLoad`
   *  seq guard as `load()`, and none of its first-paint instruments: it never
   *  calls `setLoadCount` or `setSlow`, and it never RAISES an error, so a slow
   *  or failed tick can neither surface Try again nor replace a list the nurse
   *  is reading with an error. On 200 it replaces the list AND clears a shown
   *  load error (plan-alignment, M2 round 1: a first paint that failed and a
   *  tick that then succeeded must render the rows, not keep the alert winning
   *  over a list already fetched); on 401 / 403 / 429 it halts the poll (a 401
   *  has already called `onUnauthorized` inside `api.get`, which relocks the
   *  shell); on anything else, or a throw, the shown list stays as it was.
   *  ⛔⛔ board 2707 M4 rework — A TICK BUMPS THE SEQUENCE ONLY WHEN IT APPLIES
   *  AN ANSWER. It used to take a new number at its START like `load()` does, so
   *  a tick that started while the first `load()` was still in flight made that
   *  load drop its own answer; if the tick then failed (a 500, an unreachable
   *  server) or halted (401 / 403 / 429) nothing ever filled the screen, which
   *  stayed on Loading (S5-i). Reading the number without bumping it fixed that
   *  and opened the reverse (output gate round 2): a tick that answered FIRST
   *  applied the newer list, and the older load then landed on an unchanged
   *  number and overwrote it (S5-j). So the bump moved to the 200 path: a load
   *  that started after the tick still discards it, a failed or halting tick
   *  discards nothing, and a tick that APPLIES makes every older in-flight
   *  answer (a load or another tick) stale. `my-requests-states.spec.tsx` pins
   *  both directions.
   *  simplified: two ticks in flight at once, when the OLDER one answers first:
   *  it applies and bumps, so the NEWER tick's answer is then dropped and the
   *  older list shows until the next tick; add a per-tick sequence when a tick
   *  is ever seen to outlive LIST_REFRESH_MS. */
  const refresh = async (): Promise<void> => {
    const seq = latestLoad.current;
    let res;
    try {
      res = await api.get<{ requests: RequestListItem[] }>('/api/requests');
    } catch {
      return;
    }
    if (seq !== latestLoad.current) return; // a load that started after this tick owns the screen now
    if (res.kind === 'network-error') return;
    if (res.status === 401 || res.status === 403 || res.status === 429) {
      haltedRef.current = true;
      return;
    }
    if (res.status !== 200) return;
    latestLoad.current += 1; // applied: any older in-flight load or tick is now stale (S5-j)
    setItems(res.body.requests);
    setError(null);
  };

  const tick = (): void => {
    if (haltedRef.current || document.visibilityState !== 'visible') return;
    void refresh();
  };
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') {
      haltedRef.current = false;
      tick();
    }
  };

  // ⭐⭐ T2073 M2 (D8, R-7) — ONE EFFECT: the mount fetch, the interval and the
  // visibility listener, keyed on `live` alone. While `live` (visible-and-
  // attended) the list refreshes every LIST_REFRESH_MS and at once on
  // `visibilitychange` → visible. When `live` drops (the shell's 60 s nudge has
  // fired) the cleanup removes BOTH — so after the nudge has fired the listener
  // is gone, and a return to the tab refetches on the FIRST TOUCH, not on the
  // tab switch: the `pointerdown` / `keydown` resets the nudge, `live` flips
  // back to true, and this effect's re-run is the idle→active refetch — which
  // also clears a halt, so a poll a 429 stopped resumes on the first touch even
  // on a tab that was never hidden. That is the bound R-7 promises: a tab nobody
  // is attending issues no request and so never slides the session's idle
  // clock. No `focus` listener —
  // simplified: focus listener, add when a device is seen where visibilitychange
  // does not fire on unlock.
  useEffect(() => {
    if (!firstRunDone.current) {
      firstRunDone.current = true;
      void load();
    } else if (live) {
      haltedRef.current = false;
      tick();
    }
    if (!live) return undefined;
    const id = setInterval(tick, LIST_REFRESH_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  useEffect(() => {
    if (items !== null || error !== null) return undefined;
    const t = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(t);
  }, [loadCount, items, error]);

  // ⭐ board 2732 M3 (DD8) — Cancel and Add the return time, moved verbatim into
  // `request-actions.tsx` so All requests runs the same code; this screen hands
  // in its own list read and its own network-error state.
  const { actionsFor } = useRequestActions({
    api,
    reload: load,
    onNetworkError: () => setError(sharedCopy.networkError),
  });

  function row(item: RequestListItem) {
    // ⭐ board 2707 (M4, DD5) — the Seen node, its pill word and its sentence,
    // from the one derivation `HomeRequests` reads too (`seenFor`, above). No
    // `Seen` time row: board 2732 removed it, so no `seenAt` reaches the card.
    const seenFacts = seenFor(item.status, item.seen);
    return (
      <li key={item.requestId} class="my-requests-row">
        <CardRequest
          layout="row"
          subject={item.residentDisplayName}
          // ⛔ OMITTED WHEN NULL, never a blank label - see the column comment
          // on portal.requests.job_ref.
          reference={item.jobRef !== null ? myRequestsCopy.jobRefLabel(item.jobRef) : undefined}
          destination={item.destinationName}
          // ⭐ T1683 M7 (A4) / T1717 M5 — her own answer read back as ONE phrase
          // after the destination: "Beacon Clinic and back Tue 9 Sept 14:30".
          // ⛔ THE BOOLEAN IS THE GATE, THE INSTANT IS AN ADDITION: a return job
          // with no instant reads "and back" alone; a one-way job renders
          // neither, so an operator flip to one-way hides both together.
          // ⭐⭐ T2088 M4 (ADR-0006, plan D3) — FOUR ARMS, AND THE STATE DECIDES
          // THE THIRD. A `'to-confirm'` row reads "and back time to confirm" in
          // the instant's own slot: same grammar, one space, no comma, no dash
          // (DESIGN.md §7.6 runs the phrase and its time together, and a new
          // separator on the board-1749 card would be a design decision this
          // worker may not take). ⛔ A timeless return whose state is `'unset'`
          // is NOT to be confirmed — it is a pre-v14 row, or a standing run
          // whose pattern captures no instant (DD4's recurring residual) — and
          // it keeps the bare phrase, byte-identical to before this milestone.
          returnSuffix={
            item.returnTrip
              ? ` ${myRequestsCopy.returnTripLabel} ${
                  item.returnAtState === 'to-confirm' ? myRequestsCopy.returnToConfirm : formatReturnAt(item.returnAt)
                }`.trimEnd()
              : undefined
          }
          status={item.status}
          seen={seenFacts.seen}
          waitingOn={seenFacts.waitingOn}
          when={whenFor(item)}
          stepLabel={myRequestsCopy.lifecycleLabel}
          // ⭐⭐ T2075 M3 (plan A2) — THE STAFF NAME WHEN THE ROW CARRIES ONE,
          // the two words otherwise. The `<dt>` is the card's own (`Asked for
          // by`), so the slot takes the name alone: "Asked for by Test Staff
          // Nora" on an attributed row, "Asked for by you" / "a colleague" on a
          // legacy one. The ACCOUNT's display name is still not on the wire
          // (the projection canary), so this screen still has no id or name of
          // the entering colleague to compare with or render.
          requesterSlot={item.requestedBy ?? item.askedForBy}
          // ⛔ T1749 M7 (R2) — HER OWN NOTES (and, board 2732 M2, every row's for
          // reception / admin behind the switch), and the screen does not decide
          // that either: the key is simply ABSENT where the server withholds it,
          // so the card's slot rule (`undefined` / `''` is not content) omits the
          // labelled row without a branch here.
          notesSlot={item.notes}
          // ⭐ board 2707 (M1, R-1) — WHEN SHE ASKED, as `<day> <time>` in the
          // pinned en-IE / Europe/Dublin pair. `formatReturnAt` is that exact
          // rendering of an instant (`formatWhenParts`, '' on null or garbage, so
          // the card's slot rule omits the row); its name is its first reader's.
          requestedAt={formatReturnAt(item.createdAt)}
          actions={actionsFor(item)}
        />
      </li>
    );
  }

  // ⭐⭐ T2073 M2 (D7, R-8, R-14) — THE UPCOMING SET, computed ABOVE the empty
  // branch so the empty state keys on it: a nurse whose requests are all past
  // sees the same empty state as one with none. Upcoming = dated today or later
  // in Europe/Dublin (`format.ts`'s one Dublin parse, the same the card and the
  // day buckets use). ⛔ CAPTAIN RULING: a row with NO readable day — the legacy
  // `journeyDate: null` pattern row, or a malformed date — is SHOWN, never
  // silently hidden (`d === null`); and if today's own day cannot be read
  // (`todayNo === null`: Intl threw) the filter fails OPEN and shows everything.
  const nowIso = new Date().toISOString();
  const todayNo = dublinDayNumber(nowIso);
  const upcoming = (items ?? []).filter((i) => {
    const d = dublinDayNumber(i.journeyDate);
    return d === null || todayNo === null || d >= todayNo;
  });

  // ONE shell for all four states: the heading never leaves the screen.
  let body;
  if (error) {
    body = (
      <>
        <p class="screen-error" role="alert">
          {error}
        </p>
        <p>{myRequestsCopy.loadErrorImpact}</p>
        <button type="button" class="btn btn--primary" onClick={() => void load()}>
          {sharedCopy.tryAgain}
        </button>
      </>
    );
  } else if (!items) {
    body = (
      <>
        <p class="loading-notice" role="status">
          {sharedCopy.loading}
        </p>
        {slow && (
          <button type="button" class="btn btn--primary" onClick={() => void load()}>
            {sharedCopy.tryAgain}
          </button>
        )}
      </>
    );
  } else if (upcoming.length === 0) {
    // ⭐ T2073 M2 (D9, R-8) — the shared `EmptyState`, neutral by default (this
    // emptiness is not good news and not bad; it is a list with nothing coming
    // up), `role="status"` from the component. No filter group over nothing.
    body = <EmptyState headline={myRequestsCopy.emptyState} evidence={myRequestsCopy.emptyStateNext} />;
  } else {
    // ⭐ T2073 M2 (R-14) — SOONEST FIRST: by Dublin day, then by the instant, so
    // today's rows lead. A dated row whose day cannot be read (it is under `All`
    // only) sorts LAST — it has no place in a soonest-first order.
    const dated = upcoming
      .filter((i) => i.journeyDate !== null && inDayBucket(i.journeyDate, filter, nowIso))
      .sort((a, b) => {
        const da = dublinDayNumber(a.journeyDate) ?? Number.POSITIVE_INFINITY;
        const db = dublinDayNumber(b.journeyDate) ?? Number.POSITIVE_INFINITY;
        if (da !== db) return da < db ? -1 : 1;
        const ia = a.journeyDate ?? '';
        const ib = b.journeyDate ?? '';
        return ia < ib ? -1 : ia > ib ? 1 : 0;
      });
    const repeating = upcoming.filter((i) => i.journeyDate === null);
    body = (
      <>
        <div class="my-requests-filter" role="group" aria-label={myRequestsCopy.filterLegend}>
          {/* ⭐⭐ T1749 M7 — `Chip`'s FILTER SHAPE, not a hand-classed button, and
              the carry-in from Captain's live drive (board note 3091) is the
              reason: these wore `--btn-font-size`, which is `--text-emphasis`
              (24px) on the portal, and three 24px pills wrap to two rows at
              390. A filter chip is a chip — the shared `.chip-btn` rule now
              states the body size once for both tools, which is pixel-neutral
              on the kiosk (its `--btn-font-size` already IS `--text-body`).
              ⚠️ `aria-pressed` is still the state and the paint follows it; the
              component announces it, so nothing here does. */}
          {FILTERS.map((f) => (
            <Chip
              key={f.key}
              shape="filter"
              pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.word}
            </Chip>
          ))}
        </div>
        {/* ⭐ impeccable W3 at M7: what the tap DID, announced politely. One
            status line, always present, so a filter that empties the list is
            heard and not only seen; the empty line below is the sighted copy. */}
        <p class="visually-hidden" role="status">
          {myRequestsCopy.filterShown(dated.length)}
        </p>
        {/* ⭐ impeccable W6 at M7: the dated list's heading, hidden, keeps the
            outline h1 > h2 > h3 (the cards are h3s; `Repeating trips` is the
            other h2). */}
        <h2 class="visually-hidden">{myRequestsCopy.bucketHeading[filter]}</h2>
        {dated.length === 0 && filter !== 'all' && (
          <p class="my-requests-filter-empty">
            {filter === 'today' ? myRequestsCopy.filterEmptyToday : myRequestsCopy.filterEmptyWeek}
          </p>
        )}
        {dated.length > 0 && <ul class="my-requests-list">{dated.map(row)}</ul>}
        {repeating.length > 0 && (
          <>
            <h2 class="my-requests-group">{myRequestsCopy.repeatingHeading}</h2>
            <ul class="my-requests-list">{repeating.map(row)}</ul>
          </>
        )}
      </>
    );
  }

  return (
    <div class="screen my-requests-screen">
      <h1>{myRequestsCopy.title}</h1>
      {body}
    </div>
  );
}
